import React from 'react'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import { Paper } from '@mui/material'

import { DataGrid, GridCell } from '@mui/x-data-grid'

/**
 * Función auxiliar para convertir un array de opciones de combo en un objeto lookup.
 * @param {Array<{key: any, value: string}>} combo - Array de opciones del combo.
 * @returns {Object.<string, string>} Objeto lookup donde las keys son los valores y los values son las etiquetas.
 */
const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const tableStyles = (theme) => ({
  root: {
    padding: 0,
  },
})

/**
 * @typedef {object} TableColumn
 * @property {string} field - Identificador del campo/columna.
 * @property {number} [width] - Ancho de la columna.
 * @property {string} [align] - Alineación del contenido ('left', 'center', 'right').
 * @property {boolean} [sorting] - Si es false, deshabilita el ordenamiento para esta columna.
 * @property {string} [comboId] - ID del combo en el objeto combos para lookup de valores.
 */

/**
 * @typedef {object} TableComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {TableColumn[]} columns - Array de configuraciones de columnas.
 * @property {object[]} data - Array de objetos con los datos de las filas.
 * @property {object} [combos] - Objeto con arrays de combos para lookup de valores.
 * @property {React.ComponentType} [actionsComponent] - Componente personalizado para renderizar las acciones de cada fila.
 * @property {object[]} [selectedRows] - Array de filas seleccionadas actualmente.
 * @property {boolean} [loading] - Si es true, muestra el indicador de carga en la tabla.
 * @property {function(any[]): void} [onSelectionChange] - Callback ejecutado cuando cambia la selección de filas.
 * @property {function(Event, number): void} [onChangePage] - Callback ejecutado al cambiar de página.
 * @property {function(object): void} [onChangeRowsPerPage] - Callback ejecutado al cambiar filas por página.
 * @property {function(string, 'asc' | 'desc'): void} [onOrderChange] - Callback ejecutado al cambiar el orden de una columna.
 */

/**
 * Un componente de tabla avanzada basado en DataGrid de MUI con características extendidas.
 *
 * Proporciona una tabla completa con:
 * - Selección múltiple de filas con checkboxes
 * - Ordenamiento de columnas
 * - Paginación
 * - Columna de acciones personalizable
 * - Lookup automático de valores desde combos
 * - Internacionalización de encabezados
 * - Estilos personalizados y consistentes
 * - Manejo de valores vacíos con placeholder '--'
 *
 * @component
 * @example
 * const MyAdvancedTable = () => {
 *   const [selectedRows, setSelectedRows] = useState([]);
 *
 *   const columns = [
 *     { field: 'nombre', width: 200 },
 *     { field: 'estado', comboId: 'estados', width: 150 },
 *     { field: 'fecha', width: 150 }
 *   ];
 *
 *   const data = [
 *     { id: 1, nombre: 'Solicitud 1', estado: 'activo', fecha: '2025-10-20' },
 *     { id: 2, nombre: 'Solicitud 2', estado: 'pendiente', fecha: '2025-10-21' }
 *   ];
 *
 *   const combos = {
 *     estados: {
 *       data: [
 *         { key: 'activo', value: 'Activo' },
 *         { key: 'pendiente', value: 'Pendiente' }
 *       ]
 *     }
 *   };
 *
 *   const ActionsComponent = ({ row }) => (
 *     <IconButton onClick={() => handleEdit(row)}>
 *       <EditIcon />
 *     </IconButton>
 *   );
 *
 *   return (
 *     <TableComponent
 *       columns={columns}
 *       data={data}
 *       combos={combos}
 *       actionsComponent={ActionsComponent}
 *       selectedRows={selectedRows}
 *       onSelectionChange={setSelectedRows}
 *       loading={false}
 *     />
 *   );
 * };
 *
 * @param {TableComponentProps} props - Las props del componente TableComponent.
 */
export const TableComponent = withStyles(tableStyles)(({ classes, columns, data, ...props }) => {
  const { formatMessage } = useIntl()
  const Actions = props.actionsComponent

  const formattedColumns = [
    ...columns,
    {
      field: 'actions',
      headerName: formatMessage({ id: 'global.actions' }),
      width: 80,
      align: 'center',
      sorting: false,
    },
  ].map((column) => {
    if (column.field === 'actions') return column
    return {
      ...column,
      headerName: formatMessage({ id: `pages.applications.table.columns.${column.field}.title` }),
      renderCell: (params) => {
        if (!params.value || params.value === '') {
          return <span>--</span>
        }
        return params.value
      },
      lookup:
        column.hasOwnProperty('comboId') &&
        props.combos[column.comboId] &&
        props.combos[column.comboId].data
          ? getLookup(props.combos[column.comboId].data)
          : null,
    }
  })

  const processedData = data.map((item) => {
    let isRowChecked = false
    isRowChecked = props.selectedRows.some((selectedRow) => selectedRow['id'] === item['id'])
    return {
      ...item,
      tableData: {
        ...item.tableData,
        checked: isRowChecked,
      },
    }
  })

  return (
    <DataGrid
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #e0e0e0',
        '& .MuiDataGrid-cell': {
          height: '40px',
          fontSize: '14px',
          color: '#63666a',
          padding: '0 20px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '& .MuiDataGrid-cell:focus': {
          outline: 'none',
        },
        '& .MuiDataGrid-columnHeaders': {
          height: '40px',
          fontSize: '14px',
          padding: '0 20px',
          color: '#1a171b',
          backgroundColor: 'rgba(0, 65, 101, 0.1)',
          border: 'none',
          overflow: 'visible',
        },
        '& .MuiDataGrid-sortIcon': {
          opacity: 1,
          visibility: 'visible',
        },
        '& .MuiDataGrid-row': {
          height: '40px',
          cursor: 'pointer',
          padding: 0,
          margin: 0,
        },
      }}
      columns={formattedColumns}
      rows={processedData}
      checkboxSelection={true}
      disableColumnMenu={true}
      disableRowSelectionOnClick={false}
      loading={props.loading}
      localeText={{
        MuiTablePagination: {
          labelDisplayedRows: ({ from, to, count }) => `Mostrando ${from}-${to} de ${count}`,
          labelRowsPerPage: 'registros',
        },
      }}
      slots={{
        baseContainer: ({ children }) => <Paper elevation={0}>{children}</Paper>,
        cell: (props) => (
          <GridCell {...props} classes={classes}>
            {props.field === 'actions' ? <Actions {...props} /> : props.children}
          </GridCell>
        ),
      }}
      onRowSelectionModelChange={props.onSelectionChange}
      onPaginationModelChange={(model) => {
        if (props.onChangePage) props.onChangePage(null, model.page)
        if (props.onChangeRowsPerPage)
          props.onChangeRowsPerPage({ target: { value: model.pageSize } })
      }}
      onSortModelChange={(model) => {
        if (props.onOrderChange && model.length > 0) {
          props.onOrderChange(model[0].field, model[0].sort)
        }
      }}
    />
  )
})
